import { TranslateService } from '@ngx-translate/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { MaterialService } from '../../services/material/material.service';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ValueHelperInstanceToken } from '../../interfaces/value-helper.interface';
import { MATERIAL_ORDER_PRIORITY } from '../../types/material-order-priority';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { LocalStorageNotificationMaterialOrderModel } from '../low-level/local-storage-notification-material-order.model';
import { MaterialCheckModel, ODataMaterialCheckOutModel } from '../low-level/material-check.model';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
    ODataNotificationIdentifierOutModel,
} from '../low-level/notification-identifier.model';
import { NotificationModel } from '../low-level/notification.model';
import { MultilineTextModelOptions } from '../options/multiline-text-model-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { TextModelOptions } from '../options/text-model-options';
import { MultilineTextModel, ODataTextLineOutModel } from '../primitive/multiline-text.model';
import { TextModel } from '../primitive/text.model';
import { ArrayModel } from './array.model';
import { CompositeModel } from './composite.model';
import { CseDopModel, ODataCseDopOutModel } from './cse-dop.model';
import { DropdownModel } from './dropdown.model';
import { RadioModel } from './radio.model';
import SHIPPING_PRIORITY = ConfigConstants.SHIPPING_PRIORITY;
import { AddressModel } from './address.model';
import { LocationEntry } from './material-order-fedex-location.model';
import { MaterialUtilitiesService } from '../../services/material-utilities/material-utilities.service';

export class MaterialOrderModel extends CompositeModel {
    protected configService: ConfigInterface;
    protected modelOptionsLoader: ModelOptionsLoaderService;
    protected translate: TranslateService;
    public readonly CUSTOMER_ADDRESS = 'customer';
    public readonly DOP_ADDRESS = 'dop';
    public readonly NO_ADDRESS = 'na';
    public readonly FEDEX_ADDRESS = 'fedex';
    public deliveryOptions: RadioModel<TextModel>;
    public address: CseDopModel;
    public dopNr: TextModel;
    public priority: DropdownModel<TextModel>;
    public partSet: ArrayModel<MaterialCheckModel>;
    public textInfo: MultilineTextModel;
    public dopList: DropdownModel<CseDopModel>;
    public subAddressCharLimit: number;
    private savedCustomerAddress?: CseDopModel;
    private savedFedexAddress?: CseDopModel;
    public contactMaxCharLimit: number;
    public enableStatesOfUSADropdown: boolean;
    public isPaidDelivery = false;
    public customerApprover?: TextModel;
    public sparePartsCatalogLink: string;

    public constructor(
        public notificationModel: NotificationModel,
        partSet?: MaterialCheckModel[],
        localStorageMaterialOrderModel?: LocalStorageNotificationMaterialOrderModel,
    ) {
        super('MaterialOrderModel', {});

        const injector = CustomInjector.getInjector();
        this.translate = injector.get(TranslateService);
        this.modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const valueHelperService = injector.get(ValueHelperInstanceToken);
        this.configService = injector.get(ConfigInstanceToken);
        const materialService = injector.get(MaterialService);
        const materialUtilitiesService = injector.get(MaterialUtilitiesService);

        this.subAddressCharLimit = this.configService.getMatRequestSubAddressCharLimit();
        this.contactMaxCharLimit = this.configService.getMatRequestContactCharLimit();
        this.enableStatesOfUSADropdown = this.configService.getEnableStatesOfUSADropdown();

        this.set(new TextModel('FrontId', this.configService.getUserFrontId()));
        this.set(new TextModel('MessagetypeCSE', ''));
        this.set(new TextModel('MessageVersion', ConfigConstants.messageVersion));
        this.set(new TextModel('NotificationNo', notificationModel.identifier.qmNum));
        this.set(new TextModel('TaskNumber', notificationModel.identifier.maNum));

        if (
            this.configService.getDeliveryOptionsEnabled() &&
            this.notificationModel.notificationShippingPriorities?.length > 0
        ) {
            let defaultPriorityCode = '';
            const options = valueHelperService
                .getCreateMatOrderPriority()
                .reduce((optionsArray: Array<DisplayDecorator<TextModel>>, vh) => {
                    const priorityType = this.notificationModel.notificationShippingPriorities.find(
                        (priority) => priority.code === vh.code,
                    );
                    if (priorityType) {
                        if (priorityType.type === SHIPPING_PRIORITY.DEFAULT) {
                            defaultPriorityCode = priorityType.code;
                        }
                        optionsArray.push(
                            new DisplayDecorator(
                                priorityType.type + ' - ' + vh.descText,
                                new TextModel('TypeCode', vh.code),
                            ),
                        );
                    }
                    return optionsArray;
                }, []);
            options.sort((a, b) => a.displayName.localeCompare(b.displayName));
            this.priority = <DropdownModel<TextModel>>this.set(new DropdownModel<TextModel>('PriorityCode', options));

            if (defaultPriorityCode) {
                this.priority.selectByPredicate((option) => option.value === defaultPriorityCode);
            }

            this.customerApprover = new TextModel(
                'ZPAD',
                '',
                new TextModelOptions({
                    minLength: this.configService.getDeliveryApprovalMinimumLength(),
                    required: true,
                }),
            );

            this.updateCustomerApproverVisibility();
        } else {
            const options = valueHelperService
                .getCreateMatOrderPriority()
                .map((vh) => new DisplayDecorator(vh.descText, new TextModel('TypeCode', vh.code)));
            this.priority = <DropdownModel<TextModel>>this.set(new DropdownModel<TextModel>('PriorityCode', options));
        }
        const mainEquipment = this.notificationModel.getMainEquipment();
        if (mainEquipment) {
            this.sparePartsCatalogLink = materialUtilitiesService.getSparePartCatalogMatLink(mainEquipment.matnr.value);
        }

        this.modelOptionsLoader.load(this.priority, 'CreateMaterialOrder', 'Priority');
        this.dopNr = <TextModel>this.set(new TextModel('DopNr'));
        this.modelOptionsLoader.load(this.dopNr, 'CreateMaterialOrder', 'DOPNumber');

        const dopList = materialService.getCSEDopList();
        this.dopList = new DropdownModel('DropOffPointsOptions', dopList);
        this.modelOptionsLoader.load(this.dopList, 'CreateMaterialOrder', 'DOPDropdown');

        const deliveryOptions: Array<DisplayDecorator<TextModel>> = [];
        deliveryOptions.push(
            new DisplayDecorator(
                <string>this.translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_N/A'),
                new TextModel(this.NO_ADDRESS, this.NO_ADDRESS),
            ),
        );
        const materialOrderDeliveryOptions = this.configService.getMaterialOrderDeliveryOptions();
        if (materialOrderDeliveryOptions.includes(this.CUSTOMER_ADDRESS)) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>this.translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_CUSTOMER'),
                    new TextModel(this.CUSTOMER_ADDRESS, this.CUSTOMER_ADDRESS),
                ),
            );
        }
        if (materialOrderDeliveryOptions.includes(this.DOP_ADDRESS)) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>this.translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_DOP'),
                    new TextModel(this.DOP_ADDRESS, this.DOP_ADDRESS, new TextModelOptions({ readonly: false })),
                ),
            );
        }
        if (materialOrderDeliveryOptions.includes(this.FEDEX_ADDRESS) && this.enableStatesOfUSADropdown) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>this.translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_FEDEX'),
                    new TextModel(this.FEDEX_ADDRESS, this.FEDEX_ADDRESS, new TextModelOptions({ readonly: false })),
                ),
            );
        }

        this.deliveryOptions = new RadioModel(
            'DeliveryAddress',
            deliveryOptions,
            new RadioModelOptions({ initialSelection: 0, invalidSelection: 0 }),
        );

        this.address = new CseDopModel();
        this.address.setFieldValidations('CreateMaterialOrder');

        this.partSet = <ArrayModel<MaterialCheckModel>>this.set(new ArrayModel<MaterialCheckModel>('PartSet'));
        if (typeof partSet !== 'undefined' && partSet?.length > 0) {
            this.partSet.addModels(partSet);
        }
        this.textInfo = <MultilineTextModel>this.set(new MultilineTextModel('LtextSet'));

        if (localStorageMaterialOrderModel) {
            this.deliveryOptions.selectByPredicate(
                (entry) => entry.name === localStorageMaterialOrderModel.deliveryOptions,
            );
            this.priority.selectByPredicate((entry) => entry.value === localStorageMaterialOrderModel.priority);
            this.textInfo.value = localStorageMaterialOrderModel.textInfo;
            if (this.customerApprover) {
                this.customerApprover.value = localStorageMaterialOrderModel.customerApprover ?? '';
            }
            // Older saved orders (persisted before partSet was captured) don't have it, so fall back to
            // rebuilding from `materials` - this loses fields like extUnit, but only for pre-existing data.
            this.partSet.value =
                localStorageMaterialOrderModel.partSet?.length > 0
                    ? localStorageMaterialOrderModel.partSet
                    : localStorageMaterialOrderModel.materials.map(
                          (material) =>
                              new MaterialCheckModel(
                                  material.materialIdentifier.materialNumber.value,
                                  material.quantity.value ? material.quantity.value : 1,
                                  material.storageLocation.value,
                              ),
                      );
            // Restore saved address based on selected delivery option
            if (this.isCustomerAddressOptionSelected) {
                this.savedCustomerAddress = localStorageMaterialOrderModel.address;
            } else if (this.isFedexAddressOptionSelected) {
                this.savedFedexAddress = localStorageMaterialOrderModel.address;
            } else if (
                this.deliveryOptions.selectedEntry.model.name === this.DOP_ADDRESS &&
                localStorageMaterialOrderModel.address.dropOffPoint.value
            ) {
                this.dopList.selectByPredicate(
                    (entry) =>
                        entry.model.dropOffPoint.value === localStorageMaterialOrderModel.address.dropOffPoint.value,
                );
            }
        }
        this.updateTextInfoOptions();
        this.updateAddress();
    }

    public updateCustomerApproverVisibility(): void {
        this.isPaidDelivery = this.notificationModel.notificationShippingPriorities?.some((option) => {
            return this.priority.selectedEntry?.value === option.code && option.type === SHIPPING_PRIORITY.PAYABLE;
        });
    }

    public updateTextInfoOptions(): void {
        this.textInfo.options = new MultilineTextModelOptions();
        this.modelOptionsLoader.load(this.textInfo, 'CreateMaterialOrder', 'LongTextInfo');
        const options = this.textInfo.options;
        const isTaxiSelected = this.priority.selectedEntry?.model.value === '0';
        const enableTextInfoCustomSettings = this.configService.getEnableTextInfoValidation() && isTaxiSelected;
        this.textInfo = assignModelOptions(
            this.textInfo,
            {
                ...this.textInfo.options,
                required: enableTextInfoCustomSettings ?? options.required,
                columns: enableTextInfoCustomSettings
                    ? this.configService.getMaterialOrderTextInfoLineLength()
                    : options.columns,
                lineLength: enableTextInfoCustomSettings
                    ? this.configService.getMaterialOrderTextInfoLineLength()
                    : options.lineLength,
                formatMultilineTextPattern: enableTextInfoCustomSettings
                    ? this.configService.getMaterialOrderTextInfoPattern()
                    : options.formatMultilineTextPattern,
                maxLines: enableTextInfoCustomSettings
                    ? this.configService.getMaterialOrderTextInfoMaxLines()
                    : options.maxLines,
                placeholder: enableTextInfoCustomSettings
                    ? <string>this.translate.instant('MATERIAL_ORDER_CREATE_TEXT_INFO_PLACEHOLDER')
                    : options.placeholder,
            },
            this,
        );
    }

    /**
     * Sub address : Department, Floor and Room
     * Calculates total length of Department, Floor and room fields and subtract with Max char limit to get remaining Length
     *
     * @returns number
     */
    public get remainingSubAddressLength(): number {
        const totalLengthUsed =
            this.address.department.value.length + this.address.room.value.length + this.address.floor.value.length;

        // While calculating remaining length of the whole sub address group, calculate remaining floor length too
        return this.subAddressCharLimit - totalLengthUsed;
    }

    /**
     * Contact : Phone and contact person
     * Calculates total length of Department, Floor and room fields and subtract with Max char limit to get remaining Length
     *
     * @returns number
     */
    public get remainingContactLength(): number {
        const totalLengthUsed = this.address.phone.value.length + this.address.contactPerson.value.length;
        return this.contactMaxCharLimit - totalLengthUsed;
    }

    /**
     * Used in HTML to show remaining length everytime user types something in SubAddress Department, floor and room
     * Example : Max. 28 char, Remaining 21 char
     *
     * @returns string
     */
    public get subAddressMaxCharLabel(): string {
        return this.isCustomerAddressOptionSelected
            ? this.translate.instant('MATERIAL_ORDER_SUB_ADDRESS_LIMIT_LABEL', {
                  maxChar: this.subAddressCharLimit,
                  totalRemaining: this.remainingSubAddressLength,
              })
            : '';
    }

    /**
     * Used in HTML to show remaining length everytime user types something in Phone and contact person
     * Example : Max. 29 char, Remaining 18 char.
     *
     * @returns boolean
     */
    public get contactMaxCharLabel(): string {
        return this.isCustomerAddressOptionSelected
            ? this.translate.instant('MATERIAL_ORDER_CONTACT_LIMIT_LABEL', {
                  maxChar: this.contactMaxCharLimit,
                  totalRemaining: this.remainingContactLength,
              })
            : '';
    }

    /**
     * Returns true, if selected deliveryOptions is CUSTOMER ADDRESS
     *
     * @returns boolean
     */
    public get isCustomerAddressOptionSelected(): boolean {
        return this.deliveryOptions.selectedEntry?.model.name === this.CUSTOMER_ADDRESS;
    }

    /**
     * Returns boolean if delivery option selected is FEDEX
     *
     * @returns boolean
     */
    public get isFedexAddressOptionSelected(): boolean {
        return this.deliveryOptions.selectedEntry?.model.name === this.FEDEX_ADDRESS;
    }

    /**
     * Returns boolean if delivery option selected is DOP
     *
     * @returns boolean
     */
    public get isDopAddressOptionSelected(): boolean {
        return this.deliveryOptions.selectedEntry?.model.name === this.DOP_ADDRESS;
    }

    /**
     * Returns true if the current address is a DOP address (selected from dopList).
     *
     * @returns boolean
     */
    public get isDopAddressSaved(): boolean {
        return this.isDopAddressOptionSelected && this.dopList.selectedEntry?.model === this.address;
    }

    /**
     * The max length of sub address fields, i.e. Department, Floor, Room are updated dynamically
     * This function take care of updating the Max length for the fields
     *
     * @param textModel The text model the method will modify
     * @param maxLength the value of the max length to apply
     * @returns TextModel
     */
    private updateSubAddressMaxLengthProperty(textModel: TextModel, maxLength: number): TextModel {
        // Ensure the new max length does not go below the current length of the field
        return assignModelOptions(
            textModel,
            {
                ...textModel.options,
                maxLength: maxLength,
            },
            this.address,
        );
    }

    /**
     * The max length of sub address fields, i.e. Department, Floor, Room , Phone and Contact are updated dynamically
     * This function take care of updating the Max length for the fields
     *
     * @param textModel the text model to which we want to update its max length property
     * @param remainingLength value of remaining length
     * @returns textModel
     */
    private updateMaxLengthProperty(textModel: TextModel, remainingLength: number): TextModel {
        // Ensure the new max length does not go below the current length of the field
        const maxLength = textModel.value.length + remainingLength;
        return assignModelOptions(
            textModel,
            {
                ...textModel.options,
                maxLength: maxLength,
            },
            this.address,
        );
    }

    /**
     * If combined length of department, Room  and Floor field value exceeds the limit then re-update the remaining Length as MaxLength for other fields
     */
    public checkMaxCharValidationOnSubAddress(): void {
        if (!this.isCustomerAddressOptionSelected) {
            return;
        }
        const totalLengthUsed =
            this.address.department.value.length + this.address.room.value.length + this.address.floor.value.length;
        const remainingLength = this.subAddressCharLimit - totalLengthUsed;

        this.address.setFieldValidations('CreateMaterialOrder');
        const departmentLength =
            totalLengthUsed > this.subAddressCharLimit ? remainingLength : this.address.department.options.maxLength;
        const roomLength =
            totalLengthUsed > this.subAddressCharLimit ? remainingLength : this.address.room.options.maxLength;
        const floorLength =
            totalLengthUsed > this.subAddressCharLimit ? remainingLength : this.address.floor.options.maxLength;

        this.address.department = this.updateSubAddressMaxLengthProperty(this.address.department, departmentLength);
        this.address.room = this.updateSubAddressMaxLengthProperty(this.address.room, roomLength);
        this.address.floor = this.updateSubAddressMaxLengthProperty(this.address.floor, floorLength);
    }

    /**
     * If combined length of Phone and Contact person field value exceeds the limit re-update the remaining Length as MaxLength for other fields
     */
    public checkMaxCharValidationOnContact(): void {
        if (this.isCustomerAddressOptionSelected) {
            const remainingLength = this.remainingContactLength;

            // Apply the remaining length to each field, ensuring it respects the current character count
            this.address.phone = this.updateMaxLengthProperty(this.address.phone, remainingLength);
            this.address.contactPerson = this.updateMaxLengthProperty(this.address.contactPerson, remainingLength);
        }
    }

    /**
     * Updates the material order address and related state based on the current delivery option.
     *
     * Orchestrates address resolution and applies necessary validations:
     * 1. Resolves the appropriate address (customer, FedEx, or DOP)
     * 2. Updates address state flags (isDopAddressSaved, isCustomerAddressSaved, isFedexAddressSaved)
     * 3. Applies USA state/region validation if enabled
     * 4. Validates address fields and enforces character limits
     *
     * @param fedexLocation Optional FedEx location selected by the user from the FedEx modal.
     */
    public updateAddress(fedexLocation?: LocationEntry): void {
        if (this.isCustomerAddressOptionSelected) {
            this.address = this.savedCustomerAddress ? this.savedCustomerAddress : this.buildCustomerAddress();
            // A saved draft's own statesOfUSA dropdown is only trustworthy once it actually carries a
            // selection - `CseDopModelFromJson` always creates the (empty) dropdown on restore, even
            // when the state was never resolved, so `isDefined` alone would lock in that empty
            // selection forever instead of giving the notification's own address another chance.
            this.address.statesOfUSA = this.address.statesOfUSA?.selectedEntry
                ? this.address.statesOfUSA
                : this.notificationModel.address.statesOfUSA;
        } else if (this.isFedexAddressOptionSelected) {
            this.address = fedexLocation?.Address
                ? (this.savedFedexAddress = this.buildFedexAddress(fedexLocation))
                : (this.savedFedexAddress ?? new CseDopModel());
        } else if (this.isDopAddressOptionSelected && this.dopList.selectedEntry) {
            this.address = this.dopList.selectedEntry.model;
        } else {
            this.address = new CseDopModel();
        }

        if (this.enableStatesOfUSADropdown) {
            AddressModel.updateStateOfUSA(
                this.address,
                this.address.statesOfUSA?.selectedEntry?.model?.value ?? '',
                this.isDopAddressSaved,
            );
        }

        this.address.setFieldValidations('CreateMaterialOrder');
        this.checkMaxCharValidationOnSubAddress();
        this.checkMaxCharValidationOnContact();
    }

    /**
     * Builds a FedEx address from a FedEx location entry selected by the user.
     *
     * This method constructs an address object from FedEx location data returned by the FedEx modal.
     * The address is populated with FedEx location details (street, city, state, phone, etc.)
     * and USA state validation is applied if enabled.
     *
     * @param fedexLocation The FedEx location entry selected from the FedEx modal
     * @returns {CseDopModel} Address object populated with FedEx location data
     */
    private buildFedexAddress(fedexLocation: LocationEntry): CseDopModel {
        // This splitting logic is because the Name1 field property has a max
        // length of 30 chars and cannot be changed (discussed with Raju), so
        // we split and make use of the Name2 property field to display the rest
        const description = fedexLocation.Address.Description;
        let name1 = description;
        let name2 = '';

        if (description.length > 30) {
            const lastSpaceIndex = description.lastIndexOf(' ', 30);
            const splitIndex = lastSpaceIndex === -1 ? 30 : lastSpaceIndex;

            name1 = description.substring(0, splitIndex).trim();
            name2 = description.substring(splitIndex).trim();
        }
        const address = new CseDopModel(
            '',
            '',
            name1,
            name2,
            '',
            '',
            fedexLocation.Address.Street,
            fedexLocation.Address.PostalCode,
            fedexLocation.Address.City,
            fedexLocation.Address.StateCode,
            fedexLocation.Address.ContactPhone,
            '',
            0,
            '',
            '',
            '',
            '',
        );

        AddressModel.updateStateOfUSA(address, fedexLocation.Address.StateCode, false);

        return address;
    }

    /**
     * Builds a customer address from the notification model.
     *
     * Constructs an address object from the customer's address data stored in the notification.
     *
     * @returns {CseDopModel} Address object populated with customer notification data
     */
    private buildCustomerAddress(): CseDopModel {
        return new CseDopModel(
            '',
            '',
            this.notificationModel.address.name1.value,
            this.notificationModel.address.name2.value,
            this.notificationModel.address.name1shortP58,
            this.notificationModel.address.name2shortP58,
            this.notificationModel.address.street.value,
            this.notificationModel.address.postCode.value,
            this.notificationModel.address.city.value,
            '',
            this.notificationModel.address.phone.value,
            this.notificationModel.address.department.value,
            0,
            '',
            this.notificationModel.address.floor.value,
            this.notificationModel.address.building.value,
            this.notificationModel.address.room.value,
        );
    }

    public getOutModel(): ODataNotificationMaterialOrderOutModel {
        const outModel = <ODataNotificationMaterialOrderOutModel>super.getOutModel();
        if (this.deliveryOptions.selectedEntry.model.name !== this.NO_ADDRESS) {
            outModel.AddrSet = this.address.getOutModel();
            outModel.DopNr =
                this.isCustomerAddressOptionSelected || this.isFedexAddressOptionSelected
                    ? ''
                    : this.dopList.selectedEntry.model.dropOffPoint.value;

            if (this.isDopAddressSaved) {
                // SHCNGMP58-1348 Only for NGMP58, REGION field should have the value of STATE field from DopSet
                outModel.AddrSet.Region = this.address.state.value;
            }
            if (typeof this.address.statesOfUSA !== 'undefined') {
                outModel.AddrSet.Region = this.address.statesOfUSA.selectedEntry
                    ? this.address.statesOfUSA.selectedEntry.model.value
                    : '';
            } else if (this.address.city.value.includes(';')) {
                const [city, region] = this.address.city.value.split(';', 2);
                outModel.AddrSet.City = city.trim();
                outModel.AddrSet.Region = region.trim();
            }
        }
        if (this.isPaidDelivery && this.customerApprover) {
            outModel.LtextSet.push({
                Counter: outModel.LtextSet.length,
                TextLine: 'ZPAD: ' + this.customerApprover.value,
            });
        }
        return outModel;
    }
}

export interface ODataNotificationMaterialOrderOutModel extends ODataNotificationIdentifierOutModel {
    MessagetypeCSE: string;
    MessageVersion: string;
    PriorityCode: string;
    DopNr: string;
    PartSet: ODataMaterialCheckOutModel[];
    LtextSet: ODataTextLineOutModel[];
    AddrSet: ODataCseDopOutModel;
}

export class LocalStorageMaterialOrderModel {
    public constructor(
        public notificationIdentifier: NotificationIdentifierModel,
        public priority: MATERIAL_ORDER_PRIORITY,
        public partSet: MaterialCheckModel[],
    ) {}
}

export class LocalStorageMaterialOrderModelFromJson extends LocalStorageMaterialOrderModel {
    public constructor(data: JsonLocalStorageMaterialOrderModel) {
        super(
            new NotificationIdentifierModelFromJson(data.notificationIdentifier),
            <MATERIAL_ORDER_PRIORITY>data.priority,
            data.partSet,
        );
    }
}

export interface JsonLocalStorageMaterialOrderModel {
    notificationIdentifier: JsonNotificationIdentifierModel;
    priority: string;
    partSet: MaterialCheckModel[];
}
